import type { ComponentType, SVGProps } from 'react';
import type { IssueTypeId } from '@raptor/shared';
import { ISSUE_TYPES } from '@raptor/shared';
import { Bug, FileText, MessageSquare, SlidersHorizontal, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

// Phase 5A / task 5-28. Emoji stripped from ISSUE_TYPES[id].name; visual
// identification restored via lucide icons. lucide-react is a frontend-only
// dependency — never import it into shared/backend (see icon-map contract).
export const ISSUE_TYPE_ICON: Record<IssueTypeId, IconComponent> = {
  bug: Bug,
  feature: Sparkles,
  enhancement: SlidersHorizontal,
  documentation: FileText,
  general: MessageSquare,
};

// Visual palette per type — mirrors the historical two-way conditional in
// IssueDetail/IssueListTable/Dashboard but widened to all five Phase 5
// types. Same colour rationale as StatusBadge — semantic today, tokens
// later.
const TYPE_CLASS: Record<IssueTypeId, string> = {
  bug: 'bg-red-100 text-red-800 hover:bg-red-100/80',
  feature: 'bg-blue-100 text-blue-800 hover:bg-blue-100/80',
  enhancement: 'bg-violet-100 text-violet-800 hover:bg-violet-100/80',
  documentation: 'bg-amber-100 text-amber-800 hover:bg-amber-100/80',
  general: 'bg-slate-100 text-slate-700 hover:bg-slate-100/80',
};

interface Props {
  type: IssueTypeId;
  /** When false, only the icon renders (compact list view). Default true. */
  showLabel?: boolean;
  className?: string;
}

export function IssueTypeBadge({ type, showLabel = true, className }: Props) {
  const Icon = ISSUE_TYPE_ICON[type];
  const label = ISSUE_TYPES[type].name;
  return (
    <Badge
      data-testid="issue-type-badge"
      data-type={type}
      className={cn('gap-1 pl-1.5 pr-2', TYPE_CLASS[type], className)}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {showLabel && <span>{label}</span>}
    </Badge>
  );
}
